import { API_BASE_URL } from '../constants';

interface PaymentOrderResponse {
  cf_order_id: number;
  order_id: string;
  payment_session_id: string;
  order_status: string;
  order_amount: number;
  order_currency: string;
}

interface PaymentOrderRequest {
  order_id: string;
  order_amount: number;
}

interface PaymentConfig {
  orderAmount: number;
  orderCurrency: string;
  orderId: string;
}

interface CashfreeCheckoutResult {
  error?: {
    message: string;
  };
  redirect?: boolean;
}

declare const Cashfree: any;

export const createPaymentOrder = async (config: PaymentConfig): Promise<PaymentOrderResponse> => {
  try {
    // Generate a unique order ID using timestamp and random number
    const orderId = config.orderId;

    const orderRequest: PaymentOrderRequest = {
      order_id: orderId,
      order_amount: config.orderAmount
    };

    const response = await fetch(`${API_BASE_URL}/payment/createorderpayment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderRequest),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to create payment order');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating payment order:', error);
    throw error;
  }
};

export const initializePayment = async (config: PaymentConfig) => {
  try {
    if (typeof Cashfree === 'undefined') {
      throw new Error('Cashfree SDK not loaded');
    }

    // First create the payment order
    const paymentOrder = await createPaymentOrder(config);
    console.log('Payment order created:', paymentOrder);

    const cashfree = new Cashfree('production');//SANDBOX

    // Get the current URL for the return URL
    const baseUrl = window.location.origin;
    
    // Include both payment_session_id and orderId in the return URL
    const returnUrl = `${baseUrl}/payment-status/${config.orderId}`;

    // Invoke the Checkout V3 flow with only supported parameters
    cashfree.checkout({
      paymentSessionId: paymentOrder.payment_session_id,
      redirectTarget: '_self',
      mode:'production',
      returnUrl
    }).then(function (result: CashfreeCheckoutResult) {
      if (result.error) {
        // Handle payment initialization error
        console.error('Payment initialization failed:', result.error.message);
        showFailurePopup(result.error.message, config.orderId);
      }
      if (result.redirect) {
        // User is being redirected to Cashfree's payment page
        console.log("Redirecting to Cashfree payment page");
      }
    }).catch((error: Error) => {
      console.error('Payment error:', error);
      showFailurePopup('Failed to initialize payment', config.orderId);
    });

  } catch (error) {
    console.error('Error initializing payment:', error);
    throw error;
  }
};

// Function to show success popup
export const showSuccessPopup = (orderId: string) => {
  const successPopup = document.createElement('div');
  successPopup.innerHTML = `
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h2 class="text-2xl font-bold text-green-600 mb-4">Payment Successful!</h2>
        <p class="text-gray-700 mb-6">Your payment has been processed successfully.</p>
        <button onclick="window.location.href='/order-summary/${orderId}'" class="w-full bg-minto-500 text-white py-2 px-4 rounded hover:bg-minto-600">
          Back to Order Summary
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(successPopup);
  setTimeout(() => successPopup.remove(), 5000);
};

// Function to show failure popup
export const showFailurePopup = (errorMessage: string, orderId: string) => {
  const failurePopup = document.createElement('div');
  failurePopup.innerHTML = `
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="text-center">
          <svg class="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 class="mt-4 text-2xl font-bold text-red-600 mb-4">Payment Failed</h2>
          <p class="text-gray-700 mb-6">
            ${errorMessage || 'Your payment could not be processed. Please try again.'}
          </p>
          <div class="flex flex-col gap-3">
            <button onclick="window.location.reload()" class="w-full bg-minto-500 text-white py-2 px-4 rounded hover:bg-minto-600">
              Try Again
            </button>
            <button onclick="window.location.href='/order-summary/${orderId}'" class="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50">
              Back to Order Summary
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(failurePopup);
  setTimeout(() => failurePopup.remove(), 10000);
};

// Function to handle payment status update
export const updatePaymentStatus = async (orderId: string, paymentId: string, status: 'SUCCESS' | 'FAILED', error?: string) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payment/update-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId,
        paymentId,
        status,
        error
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update payment status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating payment status:', error);
    throw error;
  }
};

export const verifyPayment = async (orderId: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/payment/verify/${orderId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to verify payment');
    }

    const data = await response.json();
    return data.status === 'PAID';
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};

export type PaymentStatusResponse =
  | string
  | {
      order_status?: string;
      status?: string;
      paymentStatus?: string;
      order_id?: string;
      order_amount?: number;
    };

// Update the checkPaymentStatus function to use orderId
export const checkPaymentStatus = async (orderId: string): Promise<PaymentStatusResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/payment/status/${orderId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch payment status');
    }

    const responseText = (await response.text()).trim();
    if (!responseText) {
      throw new Error('Empty payment status response');
    }

    try {
      const data = JSON.parse(responseText);
      console.log('Payment status response:', data);
      return data;
    } catch {
      console.log('Payment status response:', responseText);
      return responseText;
    }
  } catch (error) {
    console.error('Error checking payment status:', error);
    throw error;
  }
}; 
